// Global app-access gate state. Distinct from AUTHENTICATION (does the user have
// a valid session — owned by Cognito/Amplify): this tracks AUTHORIZATION (is the
// signed-in user provisioned in the Control Center allowlist). The Authorization
// gate sets it from the GET /auth/me probe, and the axios interceptor flips it to
// "forbidden" if any request later returns 403 (access revoked mid-session).
//
// NOT persisted (see store persistConfig whitelist) — it must re-verify against
// the backend on every load, never trust a cached "allowed" from a prior user.

import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export type AppAccessStatus = "checking" | "allowed" | "forbidden" | "unauthenticated" | "error";

interface AuthState {
	status: AppAccessStatus;
	// Signed-in user's email, for display on the access-denied screen.
	email: string | null;
}

export const initialState: AuthState = {
	status: "checking",
	email: null,
};

const authSlice = createSlice({
	name: "authSlice",
	initialState,
	reducers: {
		setAccess: (state, action: PayloadAction<{ status: AppAccessStatus; email?: string | null }>) => {
			state.status = action.payload.status;
			if (action.payload.email !== undefined) state.email = action.payload.email;
		},
		// Called by the axios 403 handler — a provisioned session that just lost
		// access (disabled/removed in the Access screen). Keeps any known email.
		setAccessForbidden: (state) => {
			state.status = "forbidden";
		},
		resetAuth: () => initialState,
	},
});

export const { resetAuth, setAccess, setAccessForbidden } = authSlice.actions;
export default authSlice.reducer;
